import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Search, Shield, User as UserIcon, Ban, CheckCircle, Key, Smartphone, MoreVertical, AlertCircle, Clock, Trash2, CreditCard, Code, ArrowUp, ArrowDown, ArrowUpDown, FlaskConical } from 'lucide-react';
import { User } from '../../types';
import Card, { CardBody } from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import ConfirmModal from '../../components/common/ConfirmModal';
import api from '../../services/api';
import { useToast } from '../../hooks/useToast';

interface TrustedDevice {
  _id: string;
  userAgent: string;
  createdAt: string;
}

export default function ManageUsers() {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'admin' | 'user' | 'blocked' | 'unverified'>('all');
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [devicesModal, setDevicesModal] = useState<{ isOpen: boolean; userId: string; userName: string; devices: TrustedDevice[] } | null>(null);
  const [blockReasonModal, setBlockReasonModal] = useState<{ isOpen: boolean; userId: string; userName: string } | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [sortField, setSortField] = useState<'name' | 'email' | 'status' | 'role' | 'devices' | 'joined'>('joined');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
    isLoading?: boolean;
  } | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!actionMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setActionMenuOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [actionMenuOpen]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleRole = (userId: string, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    setConfirmModal({
      isOpen: true,
      title: t('users.changeRole'),
      message: t('users.changeRoleMessage', { role: newRole }),
      variant: 'warning',
      onConfirm: async () => {
        setConfirmModal((prev) => prev ? { ...prev, isLoading: true } : prev);
        try {
          await api.put(`/admin/users/${userId}`, { role: newRole });
          setUsers((prev) =>
            prev.map((u) => (u._id === userId ? { ...u, role: newRole as 'admin' | 'user' } : u))
          );
        } catch (error) {
          console.error('Failed to update user role:', error);
        }
        setConfirmModal(null);
      },
    });
  };

  const handleToggleCustomPayment = async (userId: string, currentlyEnabled: boolean) => {
    const newState = !currentlyEnabled;
    try {
      await api.put(`/admin/users/${userId}/custom-payment`, { enabled: newState });
      setUsers((prev) =>
        prev.map((u) => (u._id === userId ? { ...u, customPaymentEnabled: newState } as any : u))
      );
      toast.success(newState ? t('users.customPaymentEnabled') : t('users.customPaymentDisabled'));
    } catch (error) {
      toast.error(t('users.customPaymentFailed'));
    }
  };

  const handleToggleCustomPaymentMethod = async (userId: string, currentlyEnabled: boolean) => {
    const newState = !currentlyEnabled;
    try {
      await api.put(`/admin/users/${userId}/custom-payment-method`, { enabled: newState });
      setUsers((prev) =>
        prev.map((u) => (u._id === userId ? { ...u, customPaymentMethodAllowed: newState } as any : u))
      );
      toast.success(`Custom payment method ${newState ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update custom payment method setting');
    }
  };

  const handleToggleOpenApi = async (userId: string, currentlyEnabled: boolean) => {
    const newState = !currentlyEnabled;
    try {
      await api.put(`/admin/users/${userId}/open-api`, { enabled: newState });
      setUsers((prev) =>
        prev.map((u) => (u._id === userId ? { ...u, openApiEnabled: newState } as any : u))
      );
      toast.success(newState ? t('users.openApiEnabled') : t('users.openApiDisabled'));
    } catch (error) {
      toast.error(t('users.openApiFailed'));
    }
  };

  const handleToggleTest = async (userId: string, currentlyTest: boolean) => {
    const newState = !currentlyTest;
    try {
      await api.put(`/admin/users/${userId}`, { isTest: newState });
      setUsers((prev) =>
        prev.map((u) => (u._id === userId ? { ...u, isTest: newState } : u))
      );
      toast.success(newState ? t('users.markedTest') : t('users.unmarkedTest'));
    } catch (error) {
      toast.error(t('users.testUpdateFailed'));
    }
  };

  const handleBlockUser = async (userId: string, reason: string) => {
    try {
      await api.post(`/admin/users/${userId}/block`, { reason });
      setUsers((prev) =>
        prev.map((u) => (u._id === userId ? { ...u, isBlocked: true, blockedReason: reason, trustedDeviceCount: 0 } : u))
      );
      setBlockReasonModal(null);
      setBlockReason('');
    } catch (error) {
      console.error('Failed to block user:', error);
    }
  };

  const handleUnblockUser = (userId: string, userName: string) => {
    setConfirmModal({
      isOpen: true,
      title: t('users.unblockUser'),
      message: t('users.unblockMessage', { name: userName }),
      variant: 'info',
      onConfirm: async () => {
        setConfirmModal((prev) => prev ? { ...prev, isLoading: true } : prev);
        try {
          await api.post(`/admin/users/${userId}/unblock`);
          setUsers((prev) =>
            prev.map((u) => (u._id === userId ? { ...u, isBlocked: false, blockedReason: undefined } : u))
          );
        } catch (error) {
          console.error('Failed to unblock user:', error);
        }
        setConfirmModal(null);
      },
    });
  };

  const handleResetPassword = (userId: string, userName: string) => {
    setConfirmModal({
      isOpen: true,
      title: t('users.resetPasswordTitle'),
      message: t('users.resetPasswordMessage', { name: userName }),
      variant: 'info',
      onConfirm: async () => {
        setConfirmModal((prev) => prev ? { ...prev, isLoading: true } : prev);
        try {
          await api.post(`/admin/users/${userId}/reset-password`);
          toast.success(t('users.resetPasswordSuccess'));
        } catch (error) {
          console.error('Failed to send password reset:', error);
          toast.error(t('users.resetPasswordFailed'));
        }
        setConfirmModal(null);
      },
    });
  };

  const handleViewDevices = async (userId: string, userName: string) => {
    try {
      const response = await api.get(`/admin/users/${userId}/devices`);
      setDevicesModal({ isOpen: true, userId, userName, devices: response.data });
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    }
    setActionMenuOpen(null);
  };

  const handleRevokeAllDevices = (userId: string, userName: string) => {
    setConfirmModal({
      isOpen: true,
      title: t('users.revokeAllDevices'),
      message: t('users.revokeAllMessage', { name: userName }),
      variant: 'warning',
      onConfirm: async () => {
        setConfirmModal((prev) => prev ? { ...prev, isLoading: true } : prev);
        try {
          await api.delete(`/admin/users/${userId}/devices`);
          setUsers((prev) =>
            prev.map((u) => (u._id === userId ? { ...u, trustedDeviceCount: 0 } : u))
          );
          if (devicesModal?.userId === userId) {
            setDevicesModal(null);
          }
        } catch (error) {
          console.error('Failed to revoke devices:', error);
        }
        setConfirmModal(null);
      },
    });
  };

  const handleDeleteUser = (userId: string, userName: string) => {
    setConfirmModal({
      isOpen: true,
      title: t('users.deleteUser'),
      message: t('users.deleteUserMessage', { name: userName }),
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal((prev) => prev ? { ...prev, isLoading: true } : prev);
        try {
          await api.delete(`/admin/users/${userId}`);
          setUsers((prev) => prev.filter((u) => u._id !== userId));
        } catch (error: any) {
          console.error('Failed to delete user:', error);
          toast.error(error.response?.data?.error || t('users.failedToDelete'));
        }
        setConfirmModal(null);
      },
    });
  };

  const getUserStatus = (user: User): { label: string; color: string; icon: React.ReactNode } => {
    if (user.isBlocked) {
      return { label: t('users.statusBlocked'), color: 'bg-red-50 text-red-600', icon: <Ban className="w-3 h-3 mr-1" /> };
    }
    if (!user.emailVerified) {
      return { label: t('users.statusUnverified'), color: 'bg-yellow-50 text-yellow-700', icon: <AlertCircle className="w-3 h-3 mr-1" /> };
    }
    return { label: t('users.statusActive'), color: 'bg-green-50 text-green-700', icon: <CheckCircle className="w-3 h-3 mr-1" /> };
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesFilter = true;
    if (filter === 'admin') matchesFilter = user.role === 'admin';
    else if (filter === 'user') matchesFilter = user.role === 'user';
    else if (filter === 'blocked') matchesFilter = !!user.isBlocked;
    else if (filter === 'unverified') matchesFilter = user.emailVerified === false;

    return matchesSearch && matchesFilter;
  });

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'joined' ? 'desc' : 'asc');
    }
  };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'name':
        return dir * a.name.localeCompare(b.name);
      case 'email':
        return dir * a.email.localeCompare(b.email);
      case 'status': {
        const statusOrder = (u: User) => u.isBlocked ? 2 : !u.emailVerified ? 1 : 0;
        return dir * (statusOrder(a) - statusOrder(b));
      }
      case 'role':
        return dir * a.role.localeCompare(b.role);
      case 'devices':
        return dir * ((a.trustedDeviceCount || 0) - (b.trustedDeviceCount || 0));
      case 'joined':
        return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      default:
        return 0;
    }
  });

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-50" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 text-primary-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-primary-600" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="pb-48">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('users.title')}</h1>
        <p className="text-gray-500">{t('users.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder={t('users.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {(['all', 'admin', 'user', 'blocked', 'unverified'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === tab
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-100'
              }`}
            >
              {t(`users.filter${tab.charAt(0).toUpperCase() + tab.slice(1)}`)}
            </button>
          ))}
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <Card>
          <CardBody>
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('users.noUsers')}</h3>
              <p className="text-gray-500">{t('users.noUsersDescription')}</p>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card className="overflow-visible">
          <div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {([
                    ['name', t('users.tableUser'), 'text-left'],
                    ['email', t('users.tableEmail'), 'text-left'],
                    ['status', t('users.tableStatus'), 'text-left'],
                    ['role', t('users.tableRole'), 'text-left'],
                    ['devices', t('users.tableDevices'), 'text-left'],
                    ['joined', t('users.tableJoined'), 'text-left'],
                  ] as [typeof sortField, string, string][]).map(([field, label, align]) => (
                    <th
                      key={field}
                      onClick={() => handleSort(field)}
                      className={`${align} py-4 px-6 text-sm font-medium text-gray-500 cursor-pointer select-none group hover:text-gray-700 transition-colors`}
                    >
                      <span className="inline-flex items-center">
                        {label}
                        <SortIcon field={field} />
                      </span>
                    </th>
                  ))}
                  <th className="text-right py-4 px-6 text-sm font-medium text-gray-500">{t('users.tableActions')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => {
                  const status = getUserStatus(user);
                  return (
                    <tr key={user._id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            user.isBlocked ? 'bg-red-500/20' : 'bg-primary-500/20'
                          }`}>
                            <UserIcon className={`w-5 h-5 ${user.isBlocked ? 'text-red-600' : 'text-primary-400'}`} />
                          </div>
                          <span className="font-medium text-gray-900">{user.name}</span>
                          {user.isTest && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                              {t('users.test')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-600">{user.email}</td>
                      <td className="py-4 px-6">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}
                        >
                          {status.icon}
                          {status.label}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.role === 'admin'
                              ? 'bg-purple-50 text-purple-600'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {user.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                          {user.role}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <button
                          onClick={() => handleViewDevices(user._id, user.name)}
                          className="inline-flex items-center text-gray-600 hover:text-gray-900"
                        >
                          <Smartphone className="w-4 h-4 mr-1" />
                          {user.trustedDeviceCount || 0}
                        </button>
                      </td>
                      <td className="py-4 px-6 text-gray-600">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-6 text-right relative">
                        <button
                          onClick={() => setActionMenuOpen(actionMenuOpen === user._id ? null : user._id)}
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
                        >
                          <MoreVertical className="w-5 h-5" />
                        </button>

                        {actionMenuOpen === user._id && (
                          <div ref={menuRef} className="absolute right-6 top-12 z-50 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                            <button
                              onClick={() => {
                                handleToggleRole(user._id, user.role);
                                setActionMenuOpen(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-100 flex items-center"
                            >
                              <Shield className="w-4 h-4 mr-2" />
                              {user.role === 'admin' ? t('users.makeUser') : t('users.makeAdmin')}
                            </button>

                            {user.isBlocked ? (
                              <button
                                onClick={() => {
                                  handleUnblockUser(user._id, user.name);
                                  setActionMenuOpen(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-gray-100 flex items-center"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                {t('users.unblockUser')}
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  setBlockReasonModal({ isOpen: true, userId: user._id, userName: user.name });
                                  setActionMenuOpen(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 flex items-center"
                              >
                                <Ban className="w-4 h-4 mr-2" />
                                {t('users.blockUser')}
                              </button>
                            )}

                            <button
                              onClick={() => {
                                handleResetPassword(user._id, user.name);
                                setActionMenuOpen(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-100 flex items-center"
                            >
                              <Key className="w-4 h-4 mr-2" />
                              {t('users.resetPassword')}
                            </button>

                            <button
                              onClick={() => handleViewDevices(user._id, user.name)}
                              className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-100 flex items-center"
                            >
                              <Smartphone className="w-4 h-4 mr-2" />
                              {t('users.viewDevices')}
                            </button>

                            <button
                              onClick={() => {
                                handleToggleTest(user._id, !!user.isTest);
                                setActionMenuOpen(null);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center ${user.isTest ? 'text-amber-600' : 'text-gray-600'}`}
                            >
                              <FlaskConical className="w-4 h-4 mr-2" />
                              {user.isTest ? t('users.unmarkTest') : t('users.markTest')}
                            </button>

                            {user.isCertifiedTrainer && (
                              <button
                                onClick={() => {
                                  handleToggleCustomPayment(user._id, !!(user as any).customPaymentEnabled);
                                  setActionMenuOpen(null);
                                }}
                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center ${(user as any).customPaymentEnabled ? 'text-amber-600' : 'text-gray-600'}`}
                              >
                                <CreditCard className="w-4 h-4 mr-2" />
                                {(user as any).customPaymentEnabled ? t('users.disableCustomPayment') : t('users.enableCustomPayment')}
                              </button>
                            )}

                            {user.isCertifiedTrainer && (
                              <button
                                onClick={() => {
                                  handleToggleCustomPaymentMethod(user._id, !!(user as any).customPaymentMethodAllowed);
                                  setActionMenuOpen(null);
                                }}
                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center ${(user as any).customPaymentMethodAllowed ? 'text-green-600' : 'text-gray-600'}`}
                              >
                                <CreditCard className="w-4 h-4 mr-2" />
                                {(user as any).customPaymentMethodAllowed ? 'Disable Custom Pay Method' : 'Enable Custom Pay Method'}
                              </button>
                            )}

                            {user.isCertifiedTrainer && (
                              <button
                                onClick={() => {
                                  handleToggleOpenApi(user._id, !!(user as any).openApiEnabled);
                                  setActionMenuOpen(null);
                                }}
                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center ${(user as any).openApiEnabled ? 'text-blue-600' : 'text-gray-600'}`}
                              >
                                <Code className="w-4 h-4 mr-2" />
                                {(user as any).openApiEnabled ? t('users.disableOpenApi') : t('users.enableOpenApi')}
                              </button>
                            )}

                            {(user.trustedDeviceCount || 0) > 0 && (
                              <button
                                onClick={() => {
                                  handleRevokeAllDevices(user._id, user.name);
                                  setActionMenuOpen(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-yellow-700 hover:bg-gray-100 flex items-center"
                              >
                                <AlertCircle className="w-4 h-4 mr-2" />
                                {t('users.revokeAllDevices')}
                              </button>
                            )}

                            {user.role !== 'admin' && (
                              <button
                                onClick={() => {
                                  handleDeleteUser(user._id, user.name);
                                  setActionMenuOpen(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 flex items-center border-t border-gray-200"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {t('users.deleteUser')}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(null)}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          isLoading={confirmModal.isLoading}
        />
      )}

      {/* Block Reason Modal */}
      {blockReasonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('users.blockUserTitle', { name: blockReasonModal.userName })}</h3>
            <p className="text-gray-500 text-sm mb-4">
              {t('users.blockUserDescription')}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                {t('users.blockReasonLabel')}
              </label>
              <textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder={t('users.blockReasonPlaceholder')}
                className="input h-24 resize-none"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setBlockReasonModal(null);
                  setBlockReason('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={() => handleBlockUser(blockReasonModal.userId, blockReason)}
              >
                {t('users.blockUser')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Devices Modal */}
      {devicesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('users.trustedDevices', { name: devicesModal.userName })}
            </h3>

            {devicesModal.devices.length === 0 ? (
              <p className="text-gray-500 text-center py-8">{t('users.noTrustedDevices')}</p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {devicesModal.devices.map((device) => (
                  <div key={device._id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <Smartphone className="w-5 h-5 text-gray-500" />
                        <div>
                          <p className="text-sm text-gray-900 truncate max-w-xs" title={device.userAgent}>
                            {device.userAgent.substring(0, 50)}...
                          </p>
                          <p className="text-xs text-gray-500 flex items-center mt-1">
                            <Clock className="w-3 h-3 mr-1" />
                            {t('users.addedDate', { date: new Date(device.createdAt).toLocaleDateString() })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
              {devicesModal.devices.length > 0 && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleRevokeAllDevices(devicesModal.userId, devicesModal.userName)}
                >
                  {t('users.revokeAll')}
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => setDevicesModal(null)}
                className={devicesModal.devices.length === 0 ? 'ml-auto' : ''}
              >
                {t('common.close')}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
